import React from "react";
import { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  Slider,
  Typography,
  TextField,
  Box,
} from "@mui/material";
import { Button as Btn } from "../../../components/atoms/Button";
import theme from "../../../theme";
import { FlexRow, Spacer } from "../../../components";
import { useSnackbar } from "../../../context/SnackbarContext";
import type { ColorsCodeType } from "../../../typings";
import { MdEditDocument } from "react-icons/md";
import api from "../../../api/axiosInstance";
import * as yup from "yup";

interface AdjustApplicationModalProps {
  open: boolean;
  onClose: () => void;
  scholarshipApplicationId: number;
  onRefresh: () => void;
}

const validationSchema = yup.object({
  is_special_case: yup.boolean().required("Special case status is required"),
  social_participation_score: yup
    .number()
    .min(0, "Score must be at least 0")
    .max(100, "Score cannot exceed 100")
    .required("Social participation score is required"),
});

export default function AdjustApplicationModal({
  open,
  onClose,
  scholarshipApplicationId,
  onRefresh,
}: AdjustApplicationModalProps) {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    is_special_case: false,
    social_participation_score: 50,
  });
  const [errors, setErrors] = useState<{
    is_special_case?: string;
    social_participation_score?: string;
  }>({});



  const handleChange = (field: string, value: boolean | number) => {
    setFormData({
      ...formData,
      [field]: value,
    });
    if (errors[field as keyof typeof errors]) {
      setErrors({
        ...errors,
        [field]: undefined,
      });
    }
  };

  const handleSubmit = async () => {
    try {
      await validationSchema.validate(formData, { abortEarly: false });
      setLoading(true);
      await api.post("/scholarship-applications/adjust/", {
        scholarship_application_id: scholarshipApplicationId,
        is_special_case: formData.is_special_case,
        social_participation_score: formData.social_participation_score,
      });

      showSnackbar("Application adjusted successfully!", "success");

      // Refresh the applications list
      onRefresh();

      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      if (error instanceof yup.ValidationError) {
        const validationErrors: Record<string, string> = {};
        error.inner.forEach((err) => {
          if (err.path) {
            validationErrors[err.path] = err.message;
          }
        });
        setErrors(validationErrors);
      } else {
        showSnackbar("Failed to adjust application. Please try again.", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  const scoreMarks = [
    { value: 0, label: "0" },
    { value: 25, label: "25" },
    { value: 50, label: "50" },
    { value: 75, label: "75" },
    { value: 100, label: "100" },
  ];

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          style: {
            borderRadius: "6px",
            maxWidth: "550px",
            width: "500px",
          },
        }}
      >
        <DialogTitle
          style={{
            padding: "24px 24px 16px",
            fontSize: "24px",
            fontWeight: 600,
          }}
        >
          <FlexRow align="center">
            <MdEditDocument
              size={24}
              style={{
                marginRight: "10px",
                color: theme.palette.primary.main,
              }}
            />
            Adjust Application
          </FlexRow>
        </DialogTitle>
        <DialogContent style={{ padding: "0px 24px 16px" }}>
        <Typography
            variant="body2"
            style={{
              marginBottom: "20px",
              color: theme.palette.colors.gray['500'],
            }}
          >
            Fine-tune this applicant’s social participation details and if the applicant is a special case below.
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" fontWeight={500} gutterBottom>
              Social Participation Score
            </Typography>
            <Box sx={{ px: 1 }}>
              <Slider
                value={formData.social_participation_score}
                onChange={(_, value) =>
                  handleChange("social_participation_score", value as number)
                }
                aria-labelledby="social-participation-score-slider"
                valueLabelDisplay="auto"
                step={1}
                marks={scoreMarks}
                min={0}
                max={100}
                sx={{
                  color: theme.palette.primary.main,
                  "& .MuiSlider-thumb": {
                    height: 24,
                    width: 24,
                  },
                }}
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              <TextField
                label="Score"
                type="number"
                variant="outlined"
                fullWidth
                value={formData.social_participation_score}
                onChange={(e) => {
                  const value = Number.parseInt(e.target.value);
                  if (!isNaN(value) && value >= 0 && value <= 100) {
                    handleChange("social_participation_score", value);
                  }
                }}
                InputProps={{
                  inputProps: { min: 0, max: 100 },
                }}
                error={!!errors.social_participation_score}
                helperText={errors.social_participation_score}
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_special_case}
                  onChange={(e) =>
                    handleChange("is_special_case", e.target.checked)
                  }
                  color="primary"
                />
              }
              label={
                <Typography variant="body1" fontWeight={500}>
                  Mark as Special Case
                </Typography>
              }
            />
            {errors.is_special_case && (
              <Typography color="error" variant="caption">
                {errors.is_special_case}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <FlexRow
          justify="flex-end"
          align="center"
          style={{ padding: "15px 24px 24px" }}
        >
          <Btn
            buttonColor={theme.palette.colors["black"]["600"] as ColorsCodeType}
            textColor={theme.palette.colors["white"]["400"] as ColorsCodeType}
            onClick={onClose}
          >
            Cancel
          </Btn>
          <Spacer horizontal={1} />
          <Btn onClick={handleSubmit}>
            {loading ? (
              <>
                <CircularProgress
                  size={16}
                  style={{
                    marginRight: 8,
                    color: theme.palette.colors.white[400] as string,
                  }}
                />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Btn>
        </FlexRow>
      </Dialog>

    </>
  );
}
